import { useState, useEffect, useRef } from 'react'
import {
  reportingApiClient,
  type CallReportResponse,
  type SmsReportResponse,
  type ActivityReportResponse,
  type ActionMetricsResponse,
} from '@/lib/api/reporting.client'
import { useAppAuth } from '@/contexts/app-auth.context'
import {
  getReportingDateParams,
  resolveReportingTimezone,
} from '@/utils/reporting-date-range'

export function useReportingData() {
  const {
    isSuperAdmin,
    selectedCompanyId,
    currentCompany,
    isAppContextLoading,
    isInitialized,
  } = useAppAuth()
  const [callReport, setCallReport] = useState<CallReportResponse | null>(null)
  const [smsReport, setSmsReport] = useState<SmsReportResponse | null>(null)
  const [activityReport, setActivityReport] =
    useState<ActivityReportResponse | null>(null)
  const [actionMetrics, setActionMetrics] =
    useState<ActionMetricsResponse | null>(null)
  const [isFetching, setIsFetching] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dateRange, setDateRange] = useState<string>('7d')
  const [customStartDate, setCustomStartDate] = useState<string>('')
  const [customEndDate, setCustomEndDate] = useState<string>('')
  const [appliedCustomDates, setAppliedCustomDates] = useState<{
    start: string
    end: string
  } | null>(null)
  const lastFetchedKeyRef = useRef<string | null>(null)

  const fetchReports = async (
    explicitDateRange?: string,
    customDates?: { start: string; end: string },
  ) => {
    const activeDateRange = explicitDateRange ?? dateRange
    const datesToUse = customDates ?? appliedCustomDates
    const resolvedTimezone = resolveReportingTimezone(
      currentCompany?.timezoneName,
    )

    const getFetchKey = () => {
      const companyKey = selectedCompanyId || 'default'
      const timezoneKey = resolvedTimezone
      if (activeDateRange === 'custom') {
        const applied = datesToUse
        if (!applied?.start || !applied?.end) {
          return null
        }
        return `${companyKey}:${timezoneKey}:${activeDateRange}:${applied.start}:${applied.end}`
      }
      return `${companyKey}:${timezoneKey}:${activeDateRange}`
    }

    const currentFetchKey = getFetchKey()

    if (!currentFetchKey) {
      return
    }

    // Skip if we're already fetching or if we've already fetched for this dateRange
    if (lastFetchedKeyRef.current === currentFetchKey) {
      return
    }
    setIsFetching(true)
    setError(null)
    try {
      const timezoneName = resolvedTimezone

      // Handle custom date range - use provided dates or applied dates state
      if (activeDateRange === 'custom' && datesToUse) {
        const baseParams = getReportingDateParams(
          'custom',
          timezoneName,
          datesToUse,
        )

        // Use admin endpoints if super admin with selected company
        if (isSuperAdmin && selectedCompanyId) {
          const adminParams = {
            ...baseParams,
            companyId: selectedCompanyId,
          }
          const [callData, smsData, activityData, actionData] =
            await Promise.all([
              reportingApiClient.getAdminCallReport(adminParams),
              reportingApiClient.getAdminSmsReport(adminParams),
              reportingApiClient.getAdminActivityReport(adminParams),
              reportingApiClient.getAdminActionMetrics(adminParams),
            ])
          setCallReport(callData)
          setSmsReport(smsData)
          setActivityReport(activityData)
          setActionMetrics(actionData)
        } else {
          const [callData, smsData, activityData, actionData] =
            await Promise.all([
              reportingApiClient.getCallReport(baseParams),
              reportingApiClient.getSmsReport(baseParams),
              reportingApiClient.getActivityReport(baseParams),
              reportingApiClient.getActionMetrics(baseParams),
            ])
          setCallReport(callData)
          setSmsReport(smsData)
          setActivityReport(activityData)
          setActionMetrics(actionData)
        }
        lastFetchedKeyRef.current = currentFetchKey
        return
      }
      const baseParams = getReportingDateParams(activeDateRange, timezoneName)

      // Use admin endpoints if super admin with selected company
      if (isSuperAdmin && selectedCompanyId) {
        const adminParams = {
          ...baseParams,
          companyId: selectedCompanyId,
        }
        const [callData, smsData, activityData, actionData] = await Promise.all(
          [
            reportingApiClient.getAdminCallReport(adminParams),
            reportingApiClient.getAdminSmsReport(adminParams),
            reportingApiClient.getAdminActivityReport(adminParams),
            reportingApiClient.getAdminActionMetrics(adminParams),
          ],
        )
        setCallReport(callData)
        setSmsReport(smsData)
        setActivityReport(activityData)
        setActionMetrics(actionData)
      } else {
        const [callData, smsData, activityData, actionData] = await Promise.all(
          [
            reportingApiClient.getCallReport(baseParams),
            reportingApiClient.getSmsReport(baseParams),
            reportingApiClient.getActivityReport(baseParams),
            reportingApiClient.getActionMetrics(baseParams),
          ],
        )
        setCallReport(callData)
        setSmsReport(smsData)
        setActivityReport(activityData)
        setActionMetrics(actionData)
      }
      lastFetchedKeyRef.current = currentFetchKey
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reports')
      lastFetchedKeyRef.current = null
    } finally {
      setIsFetching(false)
    }
  }

  // Fetch once after app context is initialized
  useEffect(() => {
    if (isInitialized && !isAppContextLoading) {
      fetchReports()
    }
  }, [
    isInitialized,
    isAppContextLoading,
    selectedCompanyId,
    currentCompany?.timezoneName,
  ])

  // Explicit action handler for date range changes
  const handleDateRangeChange = (newDateRange: string) => {
    // Clear custom dates if switching away from custom
    if (newDateRange !== 'custom') {
      setCustomStartDate('')
      setCustomEndDate('')
      setAppliedCustomDates(null)
    }
    setDateRange(newDateRange)
    // Explicitly fetch with new date range (pass explicitly to use new value)
    fetchReports(newDateRange)
  }

  const handleCustomDateRangeApply = () => {
    if (customStartDate && customEndDate) {
      const start = new Date(customStartDate)
      const end = new Date(customEndDate)

      if (start > end) {
        setError('Start date must be before end date')
        return
      }

      const dates = {
        start: customStartDate,
        end: customEndDate,
      }

      // Store applied dates in state (affects rendering)
      setAppliedCustomDates(dates)
      setDateRange('custom')
      // Clear the last fetched key to force a refetch
      lastFetchedKeyRef.current = null
      // Explicitly fetch with custom dates, passing them directly
      fetchReports('custom', dates)
    }
  }

  const handleCustomDateRangeCancel = () => {
    setCustomStartDate('')
    setCustomEndDate('')
    setAppliedCustomDates(null)
    // Reset to default if currently on custom range
    if (dateRange === 'custom') {
      setDateRange('7d')
      // Explicitly fetch with default range
      fetchReports('7d')
    }
  }

  return {
    callReport,
    smsReport,
    activityReport,
    actionMetrics,
    isFetching,
    error,
    dateRange,
    setDateRange: handleDateRangeChange,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    appliedCustomDates,
    handleCustomDateRangeApply,
    handleCustomDateRangeCancel,
  }
}
